import { useQueryClient } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import {
  getGetAllNdasWithSpecifiedLpsV2QueryKey,
  useUploadCoinvestNdaDocumentForSpecificLp,
  useUploadCoinvestNdaWordDocumentForSpecificLpV2,
} from "@vega-api/generated/coinvest-ndas-upload/coinvest-ndas-upload";
import { useToast } from "@vega-core/components/Toast/useToast";
import { WORD_MIME_TYPES } from "@vega-core/constants/documents";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type UseLpNdaUploadProps = {
  dealId: string;
  onSuccess?: () => void;
};

export const useLpNdaUpload = ({ dealId, onSuccess }: UseLpNdaUploadProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const handleSuccess = () => {
    showToast({
      variant: "success",
      title: m.status_success(),
      description: m.coInvestments_ndaUploadSuccess(),
    });
    queryClient.invalidateQueries({
      queryKey: getGetAllNdasWithSpecifiedLpsV2QueryKey({ dealId }),
    });
    queryClient.invalidateQueries({
      queryKey: getGetDealTrackingForGpQueryKey(dealId),
    });
    onSuccess?.();
  };

  const handleError = (error: ErrorType<unknown>) => {
    showErrorToast(error, {
      defaultMessage: m.coInvestments_error_uploadNda(),
    });
  };

  const uploadPdfMutation = useUploadCoinvestNdaDocumentForSpecificLp({
    mutation: {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  });

  const uploadWordMutation = useUploadCoinvestNdaWordDocumentForSpecificLpV2({
    mutation: {
      onSuccess: handleSuccess,
      onError: handleError,
    },
  });

  const handleUpload = (selectedLp: string, selectedFile: File) => {
    // Determine file type based on MIME type
    const isWordFile = Object.keys(WORD_MIME_TYPES).includes(selectedFile.type);

    const uploadData = {
      data: {
        dealId,
        limitedPartnerId: selectedLp,
        file: selectedFile,
      },
    };

    if (isWordFile) {
      return uploadWordMutation.mutate(uploadData);
    } else {
      return uploadPdfMutation.mutate(uploadData);
    }
  };

  return {
    handleUpload,
    isUploading: uploadPdfMutation.isPending || uploadWordMutation.isPending,
  };
};
